import { Component, OnInit, inject, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ScoButtonModule,
  ScoDialogModule,
  ScoDropdownModule,
  ScoChipsModule,
  ScoInfoboxModule,
  ScoLoadingIndicatorModule,
  TooltipModule,
  ScoCheckboxModule,
} from '@schueco/schueco-ui-components';
import { DialogRef } from '@angular/cdk/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { map, Observable, take, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  DownloadListItem,
  removeFromDownloadList,
  selectDownloadList,
  downloadList,
  downloadListSuccess,
  downloadListFailure,
  selectIsDownloadListActive,
  updateDownloadList,
  clearDownloadList,
  cancelDownload,
} from '@frontend/data-access-download-list';
import { selectCategoryCategories } from '@frontend/data-access-categories';
import { TrackingService } from '@schueco/angular-tracking';
import { LangRouterService } from '@frontend/util-router-history';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { IsTruncatedDirective } from './is-truncated.directive';

export interface DownloadListDialogResult {
  selectedDocumentIds: number[] | null;
}

@Component({
  selector: 'dc-download-list-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ScoDialogModule,
    ScoButtonModule,
    ScoDropdownModule,
    ScoChipsModule,
    ScoInfoboxModule,
    ScoCheckboxModule,
    ScoLoadingIndicatorModule,
    TranslateModule,
    FormsModule,
    DragDropModule,
    TooltipModule,
    IsTruncatedDirective,
  ],
  templateUrl: './download-list-dialog.component.html',
  styleUrl: './download-list-dialog.component.scss',
})
export class DownloadListDialogComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private actions$ = inject(Actions);
  private trackingService = inject(TrackingService);

  dialogRef = inject<DialogRef<DownloadListDialogResult>>(DialogRef);

  documents$: Observable<DownloadListItem[]> | null = null;
  hasOnlyPdfDocuments$: Observable<boolean> = this.allDocuments$.pipe(
    map((docs) => docs.length > 0 && docs.every((doc) => doc.extension === 'pdf'))
  );

  selectedDocumentIds: number[] = [];
  filteredCount = 0;
  formatFilter = 'ALL'; // 'PDF', 'WORD', etc.
  localize = inject(LangRouterService);
  downloadMode: 'zip' | 'merged_pdf' = 'zip';
  inputName = '';
  isDownloadActive = false;
  isDownloadActive$: Observable<boolean>;
  clearAfterDownload = false;
  isMobileView = false;
  isOptionsOpen = true;
  draggedIndex: number | null = null;
  sortedDocuments: DownloadListItem[] = [];

  private destroy$ = new Subject<void>();

  readonly MAX_TOTAL_SIZE_MB_ZIP = 2000;
  readonly WARNING_TOTAL_SIZE_MB_ZIP = 1500;
  readonly MAX_TOTAL_SIZE_MB_PDF = 50;
  readonly WARNING_TOTAL_SIZE_MB_PDF = 35;

  constructor() {
    this.isDownloadActive$ = this.store.select(selectIsDownloadListActive);
  }

  get allDocuments$(): Observable<DownloadListItem[]> {
    return this.store.select(selectDownloadList);
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenSizeAfterResize();
  }

  ngOnInit(): void {
    this.updateDocumentsStream();
    this.checkScreenSize();

    // Subscribe to documents$ to keep sortedDocuments in sync with store changes
    // (including changes from other tabs)
    this.documents$?.pipe(takeUntil(this.destroy$)).subscribe((docs) => {
      this.sortedDocuments = [...docs];
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateDocumentsStream(): void {
    this.documents$ = this.allDocuments$.pipe(
      map((docs) => {
        const filtered = this.formatFilter === 'ALL' ? docs : docs.filter((doc) => doc.extension === this.formatFilter);

        this.filteredCount = filtered.length;
        return filtered;
      })
    );
  }

  setFormatFilter(format: string) {
    this.formatFilter = format;
    this.updateDocumentsStream();
  }

  onRemoveFromDownloadList(documentId: number, start_page?: number, end_page?: number, assetId?: string) {
    this.store.dispatch(removeFromDownloadList({ documentId, start_page, end_page, assetId }));
  }

  onClearDownloadList() {
    this.store.dispatch(clearDownloadList());
  }

  async confirmDialog() {
    if (!this.inputName?.trim()) {
      this.inputName = '';
    }

    // Verwende die sortierte Liste:
    const listToDownload = this.sortedDocuments;
    this.isDownloadActive = true;

    this.store.dispatch(
      downloadList({ documents: listToDownload, fileName: this.inputName, downloadMode: this.downloadMode })
    );

    // Warte auf Erfolg oder Fehler
    this.actions$
      .pipe(
        ofType(downloadListSuccess, downloadListFailure, cancelDownload),
        take(1),
        withLatestFrom(this.store.select(selectCategoryCategories))
      )
      .subscribe(([action, categories]) => {
        this.isDownloadActive = false;
        if (action.type === downloadListSuccess.type) {
          for (const doc of listToDownload) {
            const categoryName = categories?.find((c) => c.id === doc.categoryId)?.name || '';
            this.trackingService.trackDownload(doc.title, categoryName);
          }
          if (this.clearAfterDownload) {
            this.store.dispatch(clearDownloadList());
            this.clearAfterDownload = false;
          }
        }
      });
  }

  cancelDialog() {
    this.dialogRef.close();
  }

  onDocumentSelected(document: DownloadListItem) {
    const basePath = `/categories/${document.categoryId}/documents/${document.documentId}/${document.language}`;
    const route = this.localize.translateRoute(basePath) as string;
    const queryParams = document.start_page_nr ? { page: document.start_page_nr } : {};

    // If we implement a route change by page scrolling,
    // we need to remove navigateByUrl({ skipLocationChange: true, replaceUrl: false }
    // to not to reload the whole page by navigation
    this.router.navigateByUrl('/', { skipLocationChange: true, replaceUrl: false }).then(() => {
      this.router.navigate([route], { queryParams }).then(() => {
        this.dialogRef.close();
      });
    });
  }

  onCancelDownload() {
    this.store.dispatch(cancelDownload());
  }

  onDownloadButtonClick(): void {
    if (this.isDownloadActive) {
      this.onCancelDownload();
    } else {
      this.confirmDialog();
    }
  }

  getTotalSize(documents: DownloadListItem[]): number {
    if (!documents?.length) return 0;
    let total = 0;
    for (const doc of documents) {
      total += Number(doc.size);
    }
    return total;
  }

  private checkScreenSize() {
    this.isMobileView = window.innerWidth <= 768;
  }

  private checkScreenSizeAfterResize() {
    if (window.innerWidth <= 768) {
      this.dialogRef.close();
      this.isMobileView = true;
    } else {
      this.isMobileView = false;
    }
  }

  toggleOptions() {
    this.isOptionsOpen = !this.isOptionsOpen;
  }

  trackByDocumentId(index: number, doc: DownloadListItem): string {
    return `${doc.documentId}-${doc.start_page ?? ''}-${doc.end_page ?? ''}`;
  }

  drop(event: CdkDragDrop<DownloadListItem[]>) {
    const docs = [...this.sortedDocuments];
    moveItemInArray(docs, event.previousIndex, event.currentIndex);
    this.sortedDocuments = docs;
    this.store.dispatch(updateDownloadList({ documents: docs }));
  }

  getMaxTotalSize(): number {
    return this.downloadMode === 'zip' ? this.MAX_TOTAL_SIZE_MB_ZIP : this.MAX_TOTAL_SIZE_MB_PDF;
  }

  getWarningTotalSize(): number {
    return this.downloadMode === 'zip' ? this.WARNING_TOTAL_SIZE_MB_ZIP : this.WARNING_TOTAL_SIZE_MB_PDF;
  }

  onClearDownloadListCheckboxChange() {
    this.clearAfterDownload = !this.clearAfterDownload;
  }
}
